/**
 * utils/errorHandler.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for turning a failed request into something a person
 * reads. Every `catch` block in this codebase calls `handleApiError` and does
 * nothing else — no screen re-parses `error.response.data`.
 *
 * WHAT COMES BACK FROM sk-company-cash-api (src/middleware/error.ts):
 *   AppError        { error: "ERR_ALREADY_REVERSED", message, details? }
 *   Zod failure     { error: "validation_error", details: { fieldErrors, formErrors } }
 *   Gate unmet      412 { error: "ERR_...", message, details: { gate } }
 *   Auth            401 { error: "missing_token" | "invalid_token" }
 *                   403 { error: "forbidden", required_scope? }
 *   Unhandled       500 { error: "internal_error" }
 *
 * The API writes a real sentence into `message` for everything a user can
 * provoke, so that message is ALWAYS preferred. The `ERR_*` copy table below
 * exists only for the paths where the message is a database-guard string —
 * "rejected by database guard: ERR_APPEND_ONLY" is true but useless.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { showToast } from '../components/Toast';
import { gateFromError, GATE_COPY, type GateKey } from '../lib/gate';

const MAX_LENGTH = 300;
const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

/** Never render markup or a scheme that a browser or webview could act on. */
const BLOCKED = /<|>|javascript:|data:/i;

function isSafeString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0 && !BLOCKED.test(value);
}

const clip = (value: string): string => value.trim().slice(0, MAX_LENGTH);

/** Field-level detail from a Zod rejection. */
interface ValidationDetails {
  gate?: string;
  fieldErrors?: Record<string, string[] | undefined>;
  formErrors?: string[];
}

/** The shape we probe. Deliberately loose — this runs on untrusted input. */
interface ApiErrorLike {
  message?: string;
  code?: string;
  response?: {
    status?: number;
    data?: {
      error?: string;
      message?: string;
      required_scope?: string;
      details?: ValidationDetails;
    };
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   Error-code copy — used ONLY when the API could not supply a sentence
═══════════════════════════════════════════════════════════════════════════ */

/**
 * The catalogue from sk-company-cash-api/src/domain/errors.ts, in the product's
 * own words. Each entry corresponds to one of the six invariants, and the
 * wording explains the RULE rather than restating the code — someone who hits
 * ERR_APPEND_ONLY needs to be told what to do instead.
 */
const ERROR_COPY: Record<string, string> = {
  // Invariant 1 — append-only ledger.
  ERR_APPEND_ONLY: 'The ledger is append-only. Post a reversing entry instead of editing this row.',
  ERR_ALREADY_REVERSED: 'This transaction has already been reversed.',
  ERR_REVERSAL_OF_REVERSAL: 'A reversal cannot itself be reversed.',
  ERR_REVERSAL_MISMATCH: 'The reversal does not match the transaction it corrects.',
  ERR_REVERSAL_TARGET_MISSING: 'The transaction being reversed no longer exists.',

  // Invariant 3 — no variance absorbed.
  ERR_VARIANCE_REASON_REQUIRED: 'Waiving a variance requires a reason.',
  ERR_VARIANCE_ROUTING_NOT_CONFIGURED:
    'Variance routing is not set, so this count has nowhere to post its difference.',

  // Invariant 4 — no cross-currency arithmetic.
  ERR_CURRENCY_MISMATCH: 'The currency does not match the assignment it belongs to.',
  ERR_CROSS_CURRENCY_SUM: 'Amounts in different currencies cannot be combined.',

  // Invariant 5 — rejections carry a reason.
  ERR_REJECT_REASON_REQUIRED: 'A rejection must say why. The requester sees this reason.',

  // Invariant 6 — reference data deactivates, never deletes.
  ERR_LAST_OPTION_VALUE: 'Keep at least one value in this list.',
  ERR_OPTION_INACTIVE: 'That option is no longer available. Pick another.',
  ERR_OPTION_UNKNOWN: 'That option is not on the list.',
  ERR_CATEGORY_REQUIRED: 'Every spend needs a category.',
  ERR_CATEGORY_INACTIVE: 'That category has been deactivated. Pick another.',

  // Onboarding + point-of-use gates.
  ERR_NOT_CONFIGURED: 'Set a base currency and an approval threshold before using this.',
  ERR_CONNECTORS_NOT_CONFIGURED: 'Payroll and Billing are not connected yet.',
  ERR_OFFBOARD_FEED_DISABLED: 'The offboarding feed is not connected yet.',

  // Approval engine.
  ERR_APPROVAL_NOT_PENDING: 'This request has already been decided.',
  ERR_APPROVER_NOT_PERMITTED: 'You are not one of the approvers this rule names.',
  ERR_ALREADY_APPROVED_BY_YOU:
    'You have already approved this. A second, different approver is required.',

  // Lifecycle.
  ERR_ASSIGNMENT_NOT_ACTIVE: 'This assignment is not active, so nothing can be posted to it.',
  ERR_NOT_A_PURPOSE_WALLET: 'Only a purpose assignment can be closed this way.',
  ERR_PURPOSE_ALREADY_CLOSED: 'This purpose assignment is already closed.',

  // Generic.
  ERR_NOT_FOUND: 'That record no longer exists. Refresh and try again.',
  ERR_IDEMPOTENCY_CONFLICT: 'This action was already recorded. Refresh to see the result.',

  // Transport / auth.
  missing_token: 'Your session has ended. Sign in again to continue.',
  invalid_token: 'Your session has ended. Sign in again to continue.',
  missing_org_context: 'Your session is missing an organisation. Sign in again.',
  missing_user_context: 'Your session is missing a user. Sign in again.',
  org_inactive: 'This organisation is not active.',
  forbidden: 'You do not have permission to do that.',
  not_found: 'That record no longer exists. Refresh and try again.',
  internal_error: 'The server had a problem completing that. Please try again.',
};

/* ═══════════════════════════════════════════════════════════════════════════
   Extraction
═══════════════════════════════════════════════════════════════════════════ */

/**
 * Flatten a Zod `validation_error` into one readable sentence. A toast that
 * says "amount minor: Required" helps far more than "validation_error".
 */
function validationMessage(details?: ValidationDetails): string | null {
  if (!details) return null;

  const form = details.formErrors?.filter(isSafeString) ?? [];
  if (form.length > 0) return clip(form.join(' '));

  const fields = Object.entries(details.fieldErrors ?? {})
    .map(([field, messages]) => {
      const first = messages?.find(isSafeString);
      return first ? `${field.replace(/_/g, ' ')}: ${first}` : null;
    })
    .filter((s): s is string => s !== null);

  return fields.length > 0 ? clip(fields.join(' · ')) : null;
}

/**
 * Extract the most specific message available, in priority order. Always
 * returns the backend's own words when it supplied any.
 */
export function getApiErrorMessage(error: unknown, fallback = DEFAULT_FALLBACK): string {
  if (!error || typeof error !== 'object') return fallback;
  const err = error as ApiErrorLike;
  const data = err.response?.data;

  // 1. A field-level validation failure — the most actionable thing we have.
  if (data?.error === 'validation_error') {
    const message = validationMessage(data.details);
    if (message) return message;
  }

  // 2. The API's own sentence, unless it is the database-guard passthrough,
  //    whose text is a constraint name rather than an explanation.
  const apiMessage = data?.message;
  if (isSafeString(apiMessage) && !apiMessage.startsWith('rejected by database guard')) {
    return clip(apiMessage);
  }

  // 3. A permission failure that names the scope it wanted. This is checked
  //    BEFORE the copy table, because ERROR_COPY has a generic entry for
  //    `forbidden` that would otherwise shadow the more specific message.
  if (data?.error === 'forbidden' && isSafeString(data.required_scope)) {
    return `You do not have permission to do that (${data.required_scope}).`;
  }

  // 4. Product copy for the error code.
  const copy = data?.error ? ERROR_COPY[data.error] : undefined;
  if (copy) return copy;

  // 5. Network / timeout / JS error.
  if (err.code === 'ECONNABORTED') return 'The request timed out. Please try again.';
  if (!err.response && isSafeString(err.message)) {
    return 'Cannot reach the server. Check your connection and try again.';
  }
  if (isSafeString(err.message)) return clip(err.message);

  return fallback;
}

/** The stable machine code, for callers that need to branch on a specific one. */
export function getApiErrorCode(error: unknown): string | null {
  const code = (error as ApiErrorLike)?.response?.data?.error;
  return isSafeString(code) ? code : null;
}

export function getApiErrorStatus(error: unknown): number | null {
  return (error as ApiErrorLike)?.response?.status ?? null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Display
═══════════════════════════════════════════════════════════════════════════ */

/**
 * Show a failed request as a toast. `title` names the action that failed
 * ("Approve failed", "Could not record the spend") and the subtitle carries
 * the backend's own explanation.
 *
 * Returns the message it displayed, so a caller that also renders the failure
 * inline does not have to extract it twice.
 */
export function handleApiError(error: unknown, title: string, fallback = DEFAULT_FALLBACK): string {
  const message = getApiErrorMessage(error, fallback);

  // A 412 is not a failure of the user's action — the workspace simply is not
  // configured yet. It gets the amber tone and the gate's own wording, and the
  // screen renders a gate bar alongside.
  const gate = gateFromError(error);
  if (gate) {
    showToast('warn', GATE_COPY[gate].message, message);
    return message;
  }

  showToast('danger', title, message);
  return message;
}

/** Which configuration gate the failure was, if it was one. */
export function gateOf(error: unknown): GateKey | null {
  return gateFromError(error);
}

/** True when the session is gone — the app should route back to sign-in. */
export function isSessionExpired(error: unknown): boolean {
  const status = getApiErrorStatus(error);
  const code = getApiErrorCode(error);
  return status === 401 || code === 'missing_token' || code === 'invalid_token';
}
